import { z } from 'zod';
/**
 * ehr#230 - the client chart's Summary tab (Chase's SUMMARY_TAB_B10 + handoff V7).
 *
 * A layout is what one person sees: the header strip fields, the cards in order, and
 * which cards are hidden. Each user can save their own ("My layout"); an admin or
 * manager saves the practice default. Values never live in a layout: client info comes
 * from the Demographics record, form answers from the client's submitted forms, and
 * card notes from the client record (contact_summary_notes).
 */
/** Client info fields usable in the header strip and on custom cards (mockup `DF`). */
export const SUMMARY_FIELD_KEYS = [
    'pref',
    'age',
    'pron',
    'payor',
    'member',
    'group',
    'phone',
    'email',
    'clin',
    'emer',
    'lang',
    'bal',
];
export const SUMMARY_FIELD_LABELS = {
    pref: 'Preferred name',
    age: 'Age (DOB)',
    pron: 'Pronouns',
    payor: 'Primary payor',
    member: 'Member ID',
    group: 'Group number',
    phone: 'Mobile phone',
    email: 'Email',
    clin: 'Primary clinician',
    emer: 'Emergency contact',
    lang: 'Languages',
    bal: 'Client balance',
};
/** The built-in Demographics cards: hide and move only (mockup `BUILTIN`). */
export const SUMMARY_BUILTIN_CARDS = [
    'contact',
    'demo',
    'guardian',
    'emergency',
    'care',
    'insurance',
    'guarantor',
];
export const SUMMARY_BUILTIN_TITLES = {
    contact: 'Contact Info',
    demo: 'Demographics',
    // ehr#648 - the section is "Family & Contacts" (the data key stays 'guardian').
    guardian: 'Family & Contacts',
    emergency: 'Emergency Contact',
    care: 'Care Team',
    insurance: 'Insurance / Payor',
    // ehr#743 (V11): the section is "Guarantor" (was "Guarantor / Responsible Party").
    guarantor: 'Guarantor',
};
/** The header strip holds at most this many fields. */
export const SUMMARY_MAX_STATS = 6;
const itemSchema = z.discriminatedUnion('t', [
    // A form field: `f` = the form's id, `q` = the field's id inside that form.
    z.object({ t: z.literal('form'), f: z.string().min(1).max(200), q: z.string().min(1).max(200) }),
    z.object({ t: z.literal('demo'), k: z.enum(SUMMARY_FIELD_KEYS) }),
    z.object({ t: z.literal('note') }),
]);
const cardSchema = z.discriminatedUnion('kind', [
    z.object({ id: z.enum(SUMMARY_BUILTIN_CARDS), kind: z.literal('builtin') }),
    z.object({
        id: z.string().min(1).max(64),
        kind: z.literal('forms'),
        title: z.string().max(80),
        items: z.array(itemSchema).max(80),
    }),
    z.object({
        id: z.string().min(1).max(64),
        kind: z.literal('custom'),
        title: z.string().max(80),
        items: z.array(itemSchema).max(80),
        /** A practice-default card this user hasn't placed yet ("New from practice default"). */
        fromAdmin: z.boolean().optional(),
    }),
]);
export const summaryLayoutSchema = z.object({
    stats: z.array(z.enum(SUMMARY_FIELD_KEYS)).max(SUMMARY_MAX_STATS),
    cards: z.array(cardSchema).max(60),
    hidden: z.array(z.string().max(64)).max(60),
});
export const summaryLayoutSaveSchema = z.object({
    scope: z.enum(['me', 'practice']),
    layout: summaryLayoutSchema,
});
/** The layout before anyone has saved one: the mockup's order, plus an empty From forms card. */
export const DEFAULT_SUMMARY_LAYOUT = {
    stats: ['pref', 'age', 'pron', 'payor', 'member'],
    cards: [
        ...SUMMARY_BUILTIN_CARDS.map((id) => ({ id, kind: 'builtin' })),
        { id: 'forms', kind: 'forms', title: 'From forms', items: [] },
    ],
    hidden: [],
};
const clone = (v) => JSON.parse(JSON.stringify(v));
/**
 * Tidy a layout read back from storage: drop duplicate cards and stats, drop hidden ids
 * that name no card, and add any built-in card the layout lacks (a new built-in reaches
 * everyone without a migration).
 */
export function normalizeSummaryLayout(layout) {
    const out = clone(layout);
    const seen = new Set();
    out.cards = out.cards.filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)));
    for (const id of SUMMARY_BUILTIN_CARDS) {
        if (!seen.has(id)) {
            out.cards.push({ id, kind: 'builtin' });
            seen.add(id);
        }
    }
    out.stats = [...new Set(out.stats)].slice(0, SUMMARY_MAX_STATS);
    out.hidden = [...new Set(out.hidden)].filter((id) => seen.has(id));
    return out;
}
/**
 * The layout a user sees (handoff 7a). Their own when they've saved one, else the
 * practice default, else the built-in default. A practice-default custom card the
 * user's own layout doesn't have yet waits in their Hidden cards tray, tagged
 * "New from practice default" (`fromAdmin`), until they show or save it.
 */
export function effectiveSummaryLayout(practice, mine) {
    const base = normalizeSummaryLayout(mine ?? practice ?? DEFAULT_SUMMARY_LAYOUT);
    if (!mine || !practice)
        return base;
    const have = new Set(base.cards.map((c) => c.id));
    for (const c of practice.cards) {
        if (c.kind !== 'custom' || have.has(c.id))
            continue;
        base.cards.push({ ...clone(c), fromAdmin: true });
        base.hidden.push(c.id);
        have.add(c.id);
    }
    return base;
}
/** Ready to save: custom cards with a blank name become "Untitled card" (handoff 7a). */
export function layoutForSave(layout) {
    const out = normalizeSummaryLayout(layout);
    out.cards = out.cards.map((c) => c.kind === 'custom' ? { ...c, title: c.title.trim() || 'Untitled card' } : c);
    return out;
}
/** One place for card names (handoff 7e): a blank custom card reads "Untitled card". */
export function summaryCardTitle(c) {
    if (c.kind === 'builtin')
        return SUMMARY_BUILTIN_TITLES[c.id];
    return c.title.trim() || (c.kind === 'forms' ? 'From forms' : 'Untitled card');
}
/** Note text saved on the client record for one card. */
export const summaryNoteSchema = z.object({ text: z.string().max(5000) });
//# sourceMappingURL=summary.js.map